import { Time } from '../types/time';
import {
  ReservableTicket,
  ReservablePackage,
  ReservableAddon,
  ReservableEventTemplate,
  ReservableEventTemplateSnapshot,
  BareEventHost,
} from '../types/reservable';
import {
  ReserveTimeline,
  ReserveTimelineEvent,
  ReserveTimelineItem,
  ReserveEvent,
  ReserveEventsByDate,
  TimeOption,
  DateOption,
  TicketSelections,
} from '../types/reserve';
import {
  BareReserveSchedule,
  BareReserveScheduleRule,
  ReserveSchedule,
  ReserveScheduleRuleParts,
  ReserveScheduleSlotPriceOverrides,
} from '../types/reserveSchedule';
import { ObjectId } from 'bson';
import { BrandAttribute, BrandLevel, BrandSegment } from '../types/brand';
import { BareEventTemplate, EventTemplate } from '../types/eventTemplate';
import { CartSession, CheckoutProgress, ReservationDraft } from '../types/cartSession';
import { ReserveCacheTimeslot, ReserveScheduleSlotSnapshot } from '../types/reserveCache';
import { forceIdToString, keyFromLabel, makeFullName, safeIdCompare } from './utils';
import crypto from 'crypto';
import {
  FlattenedReservation,
  NeedsAttentionItem,
  Reservation,
  ReservationAnalytics,
  ReservationOccurrence,
  ReservationPayment,
} from '../types/reservation';
import {
  firstOf,
  groupBy,
  GroupedItems,
  MappedItems,
  notUndefined,
  onlyUnique,
  mapBy,
} from './functional';
import { rruleFromScheduleRule, rruleCheckDate } from './rrule';
import {
  createIsoDateList,
  dateToIsoUTC,
  timeCompare,
  daysBetweenIsoDates,
  getServerDateLocal,
  doTimeRangesOverlap,
  isoToDateUTC,
  isoUTC,
  isoToDateLocal,
  formatTimeRange,
} from './time';
import { PropertyReservablesConfig, PropertyZone } from '../types/property';

export interface ReservablePipelineEvents {
  eventTemplates: ReservableEventTemplate[];
  collections: {
    segments: BrandSegment[];
    levels: BrandLevel[];
    attributes: BrandAttribute[];
    zones: PropertyZone[];
  };
}

export const DEFAULT_HOST_KEY = '_host';
export const DEFAULT_RESOURCE_KEY = '_resource';

export type ReservePaymentType = 'Charge' | 'Fee' | 'Transfer' | 'Refund';
export type ReservePaymentMethod =
  | 'Stripe'
  | 'StripeTerminal'
  | 'Cash'
  | 'Miscellaneous'
  | 'PlatformFee'
  | 'PlatformReversal'
  | 'Tax'
  | 'TaxReversal'
  | 'PropertyTransfer'
  | 'PropertyReversal'
  | 'HostTransfer'
  | 'HostReversal';

export function createDefaultTicket(): ReservableTicket {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Adult',
    description: '',
    price: 10000,
    images: [],
    fields: [
      {
        _id: new ObjectId().toHexString(),
        label: 'Full Name',
        type: 'input',
        settings: {
          type: 'text',
          options: [],
          constraints: {
            min: 0,
            max: 0,
            step: 0,
          },
        },
        required: true,
      },
    ],
  };
}

export function createDefaultPackage(): ReservablePackage {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Package',
    description: '',
    price: 10000,
    pricePerTicket: false,
    images: [],
  };
}

export function createDefaultAddon(): ReservableAddon {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Addon',
    description: '',
    price: 1000,
    pricePerTicket: false,
    images: [],
  };
}

export function createDefaultRule(): BareReserveScheduleRule {
  return {
    _id: new ObjectId().toHexString(),
    ruleFragments: createDefaultRuleFragments(),
    slots: [],
    host: undefined,
    hidden: false,
  };
}

export function createDefaultRuleFragments(): ReserveScheduleRuleParts {
  return {
    start: getServerDateLocal().iso,
    until: getServerDateLocal().iso,
    isDynamicSetPos: false,
    frequency: 'NEVER',
    byMonth: 1,
    byMonthDay: 1,
    bySetPos: 1,
    byDay: 'MO',
    endType: 'NEVER',
    count: 1,
    interval: 1,
  };
}

export function createStripeMetadata(data: {
  propertyId: string;
  pendingPaymentId?: string;
  reservationDraftIds?: string[];
  reservationDrafts?: Array<{
    id: string;
    reservationCode?: string;
  }>;
  reservation?: {
    id: string;
    code?: string;
  };
  cartSession?: {
    id: string;
    code?: string;
  };
  taxAmountCents?: number;
  taxCalculationId?: string;
}): Record<string, string> {
  const obj: Record<string, string> = {
    propertyId: forceIdToString(data.propertyId),
  };

  if (data.pendingPaymentId) {
    obj.pendingPaymentId = forceIdToString(data.pendingPaymentId);
  }

  if (data.reservation) {
    obj.reservationId = forceIdToString(data.reservation.id);
    if (data.reservation.code) {
      obj.reservationCode = forceIdToString(data.reservation.code);
    }
  }

  if (data.cartSession) {
    obj.cartSessionId = forceIdToString(data.cartSession.id);
    if (data.cartSession.code) {
      obj.cartSessionCode = forceIdToString(data.cartSession.code);
    }
  }

  if (data.reservationDraftIds) {
    obj.reservationDraftIds = data.reservationDraftIds.map(forceIdToString).join(',');
  }

  if (data.reservationDrafts && data.reservationDrafts.length > 0) {
    obj.reservationCodes = data.reservationDrafts
      .map(d => d.reservationCode)
      .filter(Boolean)
      .join(',');
  }

  obj.taxAmountCents = String(data.taxAmountCents || 0);

  if (data.taxCalculationId) {
    obj.taxCalculationId = data.taxCalculationId;
  }

  return obj;
}

export function slotsAreSimilar(slot1: ReserveCacheTimeslot, slot2: ReserveCacheTimeslot): boolean {
  return (
    slot1.time.hour === slot2.time.hour &&
    slot1.time.minute === slot2.time.minute &&
    slot1.duration === slot2.duration &&
    slot1.resource === slot2.resource &&
    slot1.host === slot2.host
  );
}

export function slotKeyWithEvent(event: string, slotKey: string) {
  return `${event}|${slotKey}`;
}

export function slotKeyFromReservation(reservation: Reservation): string {
  return buildReserveSlotKey(
    {
      date: dateToIsoUTC(reservation.date),
      resource: reservation.resource,
      host: reservation.host,
    },
    reservation.time,
    reservation.duration
  );
}

export function slotKeyFromReservationDraft(draft: ReservationDraft): string {
  return buildReserveSlotKey(
    {
      date: dateToIsoUTC(draft.date),
      resource: draft.resource,
      host: draft.host,
    },
    draft.time,
    draft.duration
  );
}

export function buildReserveSlotKey(
  opts: {
    date: string;
    resource: string | undefined;
    host: string | undefined;
  },
  startTime: Time,
  duration: number | undefined
): string {
  return (
    `${opts.date}|` +
    buildResourceSlotKeyPart({ resource: opts.resource, host: opts.host }, startTime, duration)
  );
}

export function buildResourceSlotKeyPart(
  opts: { resource: string | undefined; host: string | undefined },
  startTime: Time | undefined,
  duration: number | undefined
): string {
  return (
    `${opts.resource || DEFAULT_RESOURCE_KEY}|` +
    buildHostTimeSlotKeyPart(opts.host, startTime, duration)
  );
}

export function buildHostTimeSlotKeyPart(
  host: string | undefined,
  startTime: Time | undefined,
  duration: number | undefined
): string {
  return (
    `${host || DEFAULT_HOST_KEY}|` +
    `${startTime?.hour ?? '??'}:${startTime?.minute ?? '??'}|${duration || 0}`
  );
}

export function buildReserveDraftSlotKey(draft: ReservationDraft): string {
  return buildReserveSlotKey(
    {
      date: dateToIsoUTC(draft.date),
      resource: draft.resource,
      host: draft.host,
    },
    draft.time,
    draft.duration
  );
}

/**
 * Extract resource from a slotKey
 * SlotKey format: date|resource|host|hour:minute|duration
 * @param slotKey - The slot key to parse
 * @returns The resource ID or DEFAULT_RESOURCE_KEY if not specified
 */
export function getResourceFromSlotKey(slotKey: string): string {
  const parts = slotKey.split('|');
  return parts[1] || DEFAULT_RESOURCE_KEY;
}

/**
 * Check if two slot keys represent the same resource
 * @param slotKey1 - First slot key
 * @param slotKey2 - Second slot key
 * @returns true if both slots are for the same resource
 */
export function isSameResource(slotKey1: string, slotKey2: string): boolean {
  return getResourceFromSlotKey(slotKey1) === getResourceFromSlotKey(slotKey2);
}

export function bare(schedule: ReserveSchedule): BareReserveSchedule {
  return {
    ...schedule,
    eventTemplate: schedule.eventTemplate._id,
    rules: schedule.rules.map(rule => ({
      ...rule,
      host: rule.host?._id,
    })),
  };
}

export function getSlotSpaceStatus(
  slot: ReserveCacheTimeslot,
  ticketId?: string
): { capacity: number; spotsLeft: number } {
  return {
    capacity: slot.limits?.[ticketId || '']?.capacity ?? slot.capacity,
    spotsLeft: slot.limits?.[ticketId || '']?.spotsLeft ?? slot.spotsLeft,
  };
}

export function getScheduleRuleFromDate(
  rules: BareReserveScheduleRule[],
  dateIso: string,
  host?: string | null, // null means skip this check, undefined means treat as no host
  resource?: string | null // null means skip this check, undefined means treat as no resource
) {
  for (const rule of rules) {
    const resources = rule.resources ?? [];

    // rule must not be hidden
    if (rule.hidden) continue;

    // rule must apply to date
    if (!rruleCheckDate(rruleFromScheduleRule(rule.ruleFragments), dateIso)) continue;

    if (host !== null) {
      // if host is not specified, rule must not have a host (must be host agnostic)
      if (!host && rule.host) continue;

      // if host is specified, rule must match the host (cannot be host agnostic)
      if (host && (!rule.host || !safeIdCompare(rule.host, host))) continue;
    }

    if (resource) {
      // rule must match the resource (can be resource agnostic or include the resource)
      if (
        resources.length > 0 &&
        !resources.some(ruleResource => safeIdCompare(ruleResource, resource))
      ) {
        continue;
      }
    }

    return rule;
  }
}

export function getReservableEventsFromSchedules(
  inputDates: string[],
  reserveSchedules: BareReserveSchedule[]
) {
  const reserveEventsByDate: ReserveEventsByDate = {};

  inputDates.forEach(dateIso => {
    reserveSchedules.forEach(schedule => {
      const relevantRule = getScheduleRuleFromDate(schedule.rules, dateIso, null, null);

      relevantRule?.communications?.forEach(comm => {
        if (!reserveEventsByDate[dateIso]) {
          reserveEventsByDate[dateIso] = [];
        }
        const reserveEvent: ReserveEvent = {
          _id: comm._id,
          template: forceIdToString(schedule.eventTemplate),
          venue: forceIdToString(comm.venue),
          startTime: comm.time,
          duration: comm.duration,
          staffAllocations: [],
          hidden: false,
          day: '0',
          callToActions: {
            syncWithTemplate: true,
            values: [],
          },
        };
        reserveEventsByDate[dateIso].push(reserveEvent);
      });
    });
  });

  return reserveEventsByDate;
}

export function getReservationDraftProgress(draft: ReservationDraft): CheckoutProgress {
  const hasDateTime = !!(draft.date && draft.time);

  // Check if tickets were selected (quantity > 0)
  const hasTickets = !!(draft.tickets && draft.tickets.length > 0);

  // Check if all tickets have all required fields filled
  const hasTicketDetails = !!(
    draft.tickets &&
    draft.tickets.length > 0 &&
    draft.tickets.every(ticketSelection => {
      // Check if values object exists
      if (!ticketSelection.values) return false;

      // Check if all required fields from the ticket definition are filled
      const requiredFields = ticketSelection.ticket.fields?.filter(field => field.required) || [];

      return requiredFields.every(field => {
        const fieldKey = keyFromLabel(field.label);

        return (
          ticketSelection.values.length > 0 &&
          ticketSelection.values.every(
            v => v[fieldKey] !== undefined && v[fieldKey] !== null && v[fieldKey] !== ''
          )
        );
      });
    })
  );

  // Check if all required reservation-level fields are filled
  const requiredReservationFields =
    draft.eventSnapshot?.reservables?.fields?.filter(f => f.required) ?? [];
  const hasReservationDetails = requiredReservationFields.every(field => {
    const fieldKey = keyFromLabel(field.label);
    const val = draft.values?.[fieldKey];
    return val !== undefined && val !== null && val !== '';
  });

  const hasAddons =
    (draft.addons ?? []).reduce((sum, addonSelection) => sum + (addonSelection.quantity || 0), 0) >
    0;
  const requiresAddon = !!draft.eventSnapshot?.reservables?.requireAtLeastOneAddon;
  const hasRequiredAddons = !requiresAddon || hasAddons;
  const hasPackages = !!(draft.packages && draft.packages.length > 0);
  const canProceedToCheckout =
    hasDateTime && hasTickets && hasTicketDetails && hasReservationDetails && hasRequiredAddons;

  return {
    dateTime: {
      completed: hasDateTime,
      optional: false,
    },
    tickets: {
      completed: hasTickets,
      optional: false,
    },
    ticketDetails: {
      completed: hasTicketDetails,
      optional: false,
    },
    reservationDetails: {
      completed: hasReservationDetails,
      optional: false,
    },
    packages: {
      completed: hasPackages,
      optional: true,
    },
    addons: {
      completed: hasRequiredAddons,
      optional: true,
    },
    checkout: {
      completed: false, // Will be true after payment
      optional: false,
    },
    canProceedToCheckout,
  };
}

export function getReservationRevenue(reservation: Reservation | FlattenedReservation): number {
  const total = reservation.payments.reduce(
    (sum, p) =>
      sum +
      (p.type === 'Charge'
        ? p.amount
        : p.type === 'Refund'
          ? -p.amount
          : p.type === 'Fee'
            ? ['TaxReversal', 'PlatformReversal'].includes(p.method)
              ? -p.amount
              : 0
            : 0),
    0
  );

  if (isFlattenedReservation(reservation)) {
    const chunks = total / reservation._totalOccurrences;
    const thisChunk = !reservation._isLastOccurrence
      ? chunks
      : total - chunks * (reservation._totalOccurrences - 1);
    return thisChunk;
  }

  return total;
}

export function addGlyphsToPayment(
  price: string,
  type: ReservePaymentType,
  method: ReservePaymentMethod
): string {
  return type === 'Refund'
    ? `-${price}`
    : type === 'Charge'
      ? `+${price}`
      : method === 'HostTransfer' || method === 'PropertyTransfer'
        ? `(${price})`
        : method === 'HostReversal' || method === 'PropertyReversal'
          ? `(${price})`
          : type === 'Fee'
            ? ['TaxReversal', 'PlatformReversal'].includes(method)
              ? `+${price}`
              : `-${price}`
            : `${price}`;
}

export function getReservationAvailableFundsForRefund(
  reservation?: Reservation
): [ReservationPayment | undefined, number] {
  if (!reservation) return [undefined, 0];

  const stripeCharges = reservation.payments.filter(
    p => p.method === 'Stripe' && p.type === 'Charge' && !!p.groupId
  );

  const refundedByChargeId = reservation.payments
    .filter(p => p.method === 'Stripe' && p.type === 'Refund' && !!p.groupId)
    .reduce(
      (acc, payment) => {
        acc[payment.groupId] = (acc[payment.groupId] || 0) + payment.amount;
        return acc;
      },
      {} as Record<string, number>
    );

  const availableStripeCharge = [...stripeCharges].reverse().find(charge => {
    const grossAmount = charge.amount;
    const alreadyRefundedAmount = refundedByChargeId[charge.groupId] || 0;
    const refundableAmount = grossAmount - alreadyRefundedAmount;

    return refundableAmount > 0;
  });

  const maxRefundAmount = availableStripeCharge
    ? Math.max(
        0,
        availableStripeCharge.amount - (refundedByChargeId[availableStripeCharge.groupId] || 0)
      )
    : 0;

  return [availableStripeCharge, maxRefundAmount];
}

export interface PricingLineItem {
  id: string;
  price: number;
  name: string;
  label: string;
  description: string;
  quantity: number;
  type: 'Ticket' | 'Package' | 'Addon' | 'Fee';
  taxCode?: string;
}

interface PricingBuildingBlocks {
  eventSnapshot?: ReservableEventTemplateSnapshot;
  priceOverrides?: ReserveScheduleSlotPriceOverrides;
  tickets?: Array<{ quantity: number; ticket: { _id: string; price: number; name: string } }>;
  packages?: Array<{
    quantity: number;
    package: Pick<ReservablePackage, '_id' | 'price' | 'name' | 'pricePerTicket' | 'taxCode'>;
  }>;
  addons?: Array<{
    quantity: number;
    addon: Pick<ReservableAddon, '_id' | 'price' | 'name' | 'pricePerTicket' | 'taxCode'>;
  }>;
}

export function getTicketPriceAfterOverrides<T extends { _id: string; price: number }>(
  ticket: T,
  priceOverrides?: ReserveScheduleSlotPriceOverrides
): number {
  return priceOverrides?.[ticket._id] ?? ticket.price;
}

export function calculatePricingHelper(records: PricingBuildingBlocks[]) {
  let ticketTotal = 0;
  let packageTotal = 0;
  let addonTotal = 0;
  let feeTotal = 0;

  let ticketLineItems: PricingLineItem[] = [];
  let packageLineItems: PricingLineItem[] = [];
  let addonLineItems: PricingLineItem[] = [];
  let feeLineItems: PricingLineItem[] = [];
  let summaryLineItems: PricingLineItem[] = [];

  for (const rec of records) {
    let thisRecordTicketTotal = 0;
    let thisRecordPackageTotal = 0;
    let thisRecordAddonTotal = 0;
    let thisRecordFeeTotal = 0;

    const eventTemplate = rec.eventSnapshot;
    const totalTickets = (rec.tickets ?? []).reduce((sum, t) => sum + t.quantity, 0);

    for (const ticketSelection of rec.tickets ?? []) {
      const ticketBasePrice = getTicketPriceAfterOverrides(
        ticketSelection.ticket,
        rec.priceOverrides
      );
      const ticketSumPrice = ticketBasePrice * ticketSelection.quantity;

      const item: PricingLineItem = {
        id: ticketSelection.ticket._id,
        type: 'Ticket',
        price: ticketBasePrice,
        name: ticketSelection.ticket.name,
        label: `Ticket: ${ticketSelection.ticket.name}`,
        description: eventTemplate?.title ?? 'Experience Ticket',
        quantity: ticketSelection.quantity,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      ticketLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordTicketTotal += ticketSumPrice;
    }

    // Calculate from packages
    for (const packageSelection of rec.packages ?? []) {
      const packageBasePrice =
        packageSelection.package.price *
        (packageSelection.package.pricePerTicket ? totalTickets : 1);
      const packageSumPrice = packageBasePrice * packageSelection.quantity;

      const item: PricingLineItem = {
        id: packageSelection.package._id,
        type: 'Package',
        price: packageBasePrice,
        name: packageSelection.package.name,
        label: `Package: ${packageSelection.package.name}`,
        description: eventTemplate?.title ?? 'Experience Package',
        quantity: packageSelection.quantity,
        // taxCode: packageSelection.package.taxCode,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      packageLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordPackageTotal += packageSumPrice;
    }

    // Calculate from addons
    for (const addonSelection of rec.addons ?? []) {
      const addonBasePrice =
        addonSelection.addon.price * (addonSelection.addon.pricePerTicket ? totalTickets : 1);
      const addonSumPrice = addonBasePrice * addonSelection.quantity;

      const item: PricingLineItem = {
        id: addonSelection.addon._id,
        type: 'Addon',
        price: addonBasePrice,
        name: addonSelection.addon.name,
        label: `Addon: ${addonSelection.addon.name}`,
        description: eventTemplate?.title ?? 'Experience Addon',
        quantity: addonSelection.quantity,
        // taxCode: addonSelection.addon.taxCode,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      addonLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordAddonTotal += addonSumPrice;
    }

    // Calculate gratuity fees
    const gratuityFeePercentage = eventTemplate?.reservables?.gratuityFeePercentage ?? 0;
    const gratuityFeeFlatAmountCents = eventTemplate?.reservables?.gratuityFeeFlatAmountCents ?? 0;

    if (gratuityFeePercentage > 0 || gratuityFeeFlatAmountCents > 0) {
      const gratuitiesBasePrice = Math.ceil(
        (gratuityFeePercentage / 100) *
          (thisRecordTicketTotal + thisRecordPackageTotal + thisRecordAddonTotal) +
          gratuityFeeFlatAmountCents
      );
      const gratuitiesSumPrice = gratuitiesBasePrice; // quantity is always 1 for fees

      const item: PricingLineItem = {
        id: 'gratuity-fee',
        type: 'Fee',
        price: gratuitiesSumPrice,
        name: 'Gratuity Fee',
        label: 'Gratuity Fee',
        description: eventTemplate?.title ?? 'Experience Addon',
        quantity: 1,
        taxCode: eventTemplate?.reservables?.taxCode,
      };

      feeLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordFeeTotal += gratuitiesSumPrice;
    }

    ticketTotal += thisRecordTicketTotal;
    packageTotal += thisRecordPackageTotal;
    addonTotal += thisRecordAddonTotal;
    feeTotal += thisRecordFeeTotal;
  }

  const finalTotal = ticketTotal + packageTotal + addonTotal + feeTotal;

  return {
    ticketTotal,
    packageTotal,
    addonTotal,
    feeTotal,
    finalTotal,
    ticketLineItems,
    packageLineItems,
    addonLineItems,
    feeLineItems,
    summaryLineItems,
  };
}

export function calculateReservationDraftPricing(drafts: ReservationDraft[]) {
  return calculatePricingHelper(
    drafts.map(d => ({
      tickets: d.tickets,
      packages: d.packages,
      addons: d.addons,
      eventSnapshot: d.eventSnapshot,
      priceOverrides: d.slotSnapshot?.priceOverrides,
    }))
  );
}

export function calculateReservationPricing(reservations: Reservation[]) {
  const pricingRecords = reservations.map(r => {
    const { eventSnapshot } = r;

    const ticketOpts = eventSnapshot.reservables.tickets;
    const ticketOptsDict = mapBy(ticketOpts, t => t._id);

    const packageOpts = eventSnapshot.reservables.packages;
    const packageOptsDict = mapBy(packageOpts, p => p._id);

    const addonOpts = eventSnapshot.reservables.addons;
    const addonOptsDict = mapBy(addonOpts, a => a._id);

    const ticketGroups = groupBy(r.tickets, t => t.ticketId);

    const mappedItem: PricingBuildingBlocks = {
      eventSnapshot,
      priceOverrides: r.priceOverrides,
      tickets: Object.keys(ticketGroups).map(ticketId => ({
        quantity: ticketGroups[ticketId].length,
        ticket: {
          _id: ticketId,
          price: ticketOptsDict[ticketId]?.price || 0,
          name: ticketOptsDict[ticketId]?.name || 'Unknown Ticket',
        },
      })),
      packages: r.packages.map(p => ({
        quantity: p.quantity,
        package: {
          _id: p.packageId,
          price: packageOptsDict[p.packageId]?.price || 0,
          name: packageOptsDict[p.packageId]?.name || 'Unknown Package',
          pricePerTicket: packageOptsDict[p.packageId]?.pricePerTicket || false,
        },
      })),
      addons: r.addons.map(a => ({
        quantity: a.quantity,
        addon: {
          _id: a.addonId,
          price: addonOptsDict[a.addonId]?.price || 0,
          name: addonOptsDict[a.addonId]?.name || 'Unknown Addon',
          pricePerTicket: addonOptsDict[a.addonId]?.pricePerTicket || false,
        },
      })),
    };

    return mappedItem;
  });

  return calculatePricingHelper(pricingRecords);
}

export function calculateServiceFeeLineItem(
  lineItems: PricingLineItem[],
  propResConfig: PropertyReservablesConfig | undefined
) {
  const serviceFeeFlatAmountCents = propResConfig?.serviceFeeFlatAmountCents ?? 0;
  const serviceFeePercentage = propResConfig?.serviceFeePercentage ?? 0;

  const finalTotal = lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  let serviceFeeItem: PricingLineItem | undefined;

  if (serviceFeeFlatAmountCents > 0 || serviceFeePercentage > 0) {
    const serviceFeeCents = Math.ceil(
      (serviceFeePercentage / 100) * finalTotal + serviceFeeFlatAmountCents
    );
    serviceFeeItem = {
      id: 'service-fee',
      price: serviceFeeCents,
      name: 'Service Fee',
      label: 'Service Fee',
      description: '',
      quantity: 1,
      type: 'Fee',
    };
  }

  return serviceFeeItem;
}

export function cartSessionCanCheckout(
  drafts: ReservationDraft[],
  cartSession: CartSession
): boolean {
  const draftsOk =
    drafts.map(d => getReservationDraftProgress(d)).findIndex(p => !p.canProceedToCheckout) < 0;

  const cartOk =
    !!cartSession.customer &&
    !!cartSession.customer.email &&
    !!cartSession.customer.phone &&
    !!cartSession.customer.firstName &&
    !!cartSession.customer.lastName;

  return draftsOk && cartOk;
}

export function createReservationCode(): string {
  const base = Date.now().toString();
  const salt = Math.floor(Math.random() * 1e6).toString();
  const hash = crypto
    .createHash('sha256')
    .update(base + salt)
    .digest('base64')
    .replace(/[^A-Z0-9]/gi, '')
    .substring(0, 8)
    .toUpperCase();
  return `RZ-${hash}`;
}

export function createReservationCartCode(): string {
  const base = Date.now().toString();
  const salt = Math.floor(Math.random() * 1e6).toString();
  const hash = crypto
    .createHash('sha256')
    .update(base + salt)
    .digest('base64')
    .replace(/[^A-Z0-9]/gi, '')
    .substring(0, 8)
    .toUpperCase();
  return `RC-${hash}`;
}

export function calculateReservationAnalytics(
  reservations: Array<Reservation | FlattenedReservation>
): ReservationAnalytics {
  let totalRevenue = 0;

  let numOfTickets = 0;
  let numOfPackages = 0;
  let numOfAddons = 0;

  let ticketTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};
  let packageTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};
  let addonTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};

  reservations.forEach(reservation => {
    totalRevenue += getReservationRevenue(reservation);

    if (reservation.status === 'cancelled') return;

    const customer = makeFullName(reservation.customer.firstName, reservation.customer.lastName);
    const pricing = calculateReservationPricing([reservation]);

    numOfTickets += reservation.tickets.length;
    numOfPackages += reservation.packages.reduce((sum, p) => sum + p.quantity, 0);
    numOfAddons += reservation.addons.reduce((sum, a) => sum + a.quantity, 0);

    pricing.ticketLineItems.forEach(({ id, name, quantity }) => {
      if (!ticketTally[id]) ticketTally[id] = [];
      ticketTally[id].push({
        name,
        quantity,
        customer,
      });
    });

    pricing.packageLineItems.forEach(({ id, name, quantity }) => {
      if (!packageTally[id]) packageTally[id] = [];
      packageTally[id].push({
        name,
        quantity,
        customer,
      });
    });

    pricing.addonLineItems.forEach(({ id, name, quantity }) => {
      if (!addonTally[id]) addonTally[id] = [];
      addonTally[id].push({
        name,
        quantity,
        customer,
      });
    });
  });

  const tally: ReservationAnalytics['tally'] = {
    tickets: Object.entries(ticketTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Ticket',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
    packages: Object.entries(packageTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Package',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
    addons: Object.entries(addonTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Addon',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
  };

  let popularity: ReservationAnalytics['popularity'] = {
    popularTicket: tally.tickets.find(
      t => t.total === Math.max(...tally.tickets.map(tt => tt.total))
    ),
    popularPackage: tally.packages.find(
      p => p.total === Math.max(...tally.packages.map(pp => pp.total))
    ),
    popularAddon: tally.addons.find(a => a.total === Math.max(...tally.addons.map(aa => aa.total))),
  };

  return {
    totalRevenue,
    numOfTickets,
    numOfPackages,
    numOfAddons,
    tally,
    popularity,
  };
}

export function isReservableEvent(
  eventTemplate?: BareEventTemplate | null
): eventTemplate is ReservableEventTemplate {
  return !eventTemplate?.archived && (eventTemplate?.reservables?.tickets ?? []).length > 0;
}

export function isReservablePopulatedEvent(eventTemplate?: EventTemplate | null) {
  return !eventTemplate?.archived && (eventTemplate?.reservables?.tickets ?? []).length > 0;
}

export function buildCalendarMarkers<T extends { time: Time; duration?: number; host?: string }>(
  dateIso: string,
  timeslotsByDate: GroupedItems<ReserveCacheTimeslot>,
  ticketQuantities?: Record<string, number>,
  specificTimeOption?: T,
  specificResource?: string
): string[] {
  let hasExactOption = false;
  let hasOtherOptions = false;

  function checkAvailable(slot: ReserveCacheTimeslot) {
    let check = false;

    if (
      specificTimeOption &&
      buildHostTimeSlotKeyPart(
        specificTimeOption.host,
        specificTimeOption.time,
        specificTimeOption.duration
      ) !== buildHostTimeSlotKeyPart(slot.host, slot.time, slot.duration)
    ) {
      return check;
    }

    // Check specific resource
    if (specificResource && slot.resource !== specificResource) {
      return check;
    }

    check =
      getSlotSpaceStatus(slot).spotsLeft > 0 &&
      Object.keys(ticketQuantities || {}).every(
        ticketId => !checkTicketsSelectedAreAvailable(ticketId, slot, ticketQuantities || {})
      );

    return check;
  }

  (timeslotsByDate[dateIso] ?? []).forEach(slot => {
    hasExactOption ||= checkAvailable(slot);
    hasOtherOptions = slot.spotsLeft > 0;
  });

  // Return markers for the specific date
  return hasExactOption ? ['#22c55e'] : hasOtherOptions ? ['#facc15'] : ['#ef4444'];
}

export function checkTicketsSelectedAreAvailable(
  ticketId: string,
  selectedSlot: ReserveCacheTimeslot,
  ticketQuantities: Record<string, number>,
  ticketReservationLimits?: Record<string, number>
): string | undefined {
  const thisTicketQty = ticketQuantities[ticketId] ?? 0;
  const allTicketsQty = Object.values(ticketQuantities).reduce((sum, v) => sum + v, 0);
  const hasQty = thisTicketQty > 0;

  const reservationLimit = ticketReservationLimits?.[ticketId];
  if (hasQty && reservationLimit !== undefined && thisTicketQty > reservationLimit) {
    return 'Exceeds the limit per reservation';
  }

  const spaceStatus = getSlotSpaceStatus(selectedSlot);
  const ticketSpaceStatus = getSlotSpaceStatus(selectedSlot, ticketId);

  const individualCheck = thisTicketQty > ticketSpaceStatus.spotsLeft;
  const sumCheck = allTicketsQty > spaceStatus.spotsLeft;

  return hasQty && (individualCheck || sumCheck) ? 'Not enough availability' : undefined;
}

export function snapshotFromTimeslot(timeslot: ReserveCacheTimeslot): ReserveScheduleSlotSnapshot {
  return {
    slotKey: timeslot.slotKey,
    capacity: timeslot.capacity,
    spotsLeft: timeslot.spotsLeft,
    priceOverrides: timeslot.priceOverrides,
    limits: timeslot.limits,
  };
}

export function snapshotFromEventTemplate(
  event: ReservableEventTemplate
): ReservableEventTemplateSnapshot {
  return {
    _id: new ObjectId().toHexString(),
    templateId: event._id,
    title: event.title,
    description: event.description,
    reservables: {
      tickets: event.reservables.tickets,
      packages: event.reservables.packages,
      addons: event.reservables.addons,
      requireAtLeastOneAddon: !!event.reservables.requireAtLeastOneAddon,
      meetingPoint: event.reservables.meetingPoint,
      gratuityFeeFlatAmountCents: event.reservables.gratuityFeeFlatAmountCents,
      gratuityFeePercentage: event.reservables.gratuityFeePercentage,
      hostPercentage: event.reservables.hostPercentage,
      experienceType: event.reservables.experienceType,
      fields: event.reservables.fields,
      taxCode: event.reservables.taxCode,
      disclaimer: event.reservables.disclaimer,
      externalId: event.reservables.externalId,
    },
  };
}

export function buildReserveTimeline(
  from: string,
  to: string,
  schedules: BareReserveSchedule[],
  eventTemplates: BareEventTemplate[],
  reservations: Reservation[],
  bypassDayChecks?: boolean
) {
  const eventTemplateDict = mapBy(eventTemplates, t => t._id);

  const timelines: ReserveTimeline[] = [];
  const dates = createIsoDateList(from, daysBetweenIsoDates(from, to) + 1);

  const activeSchedules = firstOf(
    schedules.filter(s => !s.deleted),
    s => s.eventTemplate
  );

  // fill reservations into timeline
  const reservationsByDate = groupBy(flattenReservations(reservations), r => dateToIsoUTC(r.date));

  dates.forEach(date => {
    const timelineEvents: ReserveTimelineEvent[] = [];

    activeSchedules.forEach(schedule => {
      const template = eventTemplateDict[schedule.eventTemplate];
      if (!isReservableEvent(template)) return;

      if (!bypassDayChecks) {
        const { bookingOpenDays, bookingWindowDays } = template.reservables;

        // if booking window days is provided, then the current date must be
        // at least that many days in the future from today
        if (bookingWindowDays !== undefined) {
          const daysUntil = daysBetweenIsoDates(isoUTC(), date);
          if (daysUntil < bookingWindowDays) return;
        }

        // if booking open days is provided, then the current date must be within
        // that many days from the event date
        if (bookingOpenDays !== undefined) {
          const daysFrom = daysBetweenIsoDates(isoUTC(), date);
          if (daysFrom > bookingOpenDays) return;
        }
      }

      const timelineItems: ReserveTimelineItem[] = [];
      const isResourceType = template.reservables.experienceType === 'resource';

      // we must consider all host and the non-hosted rules separately
      const hosts = [DEFAULT_HOST_KEY, ...schedule.rules.map(r => r.host)]
        .filter(notUndefined)
        .filter(onlyUnique)
        .map(forceIdToString);

      // we consider all resources if it's a resource-type experience, otherwise
      // we just use a single "default" resource which skips the resource check
      const resources = (
        isResourceType ? template.reservables.tickets.map(t => t._id) : [DEFAULT_RESOURCE_KEY]
      )
        .filter(notUndefined)
        .filter(onlyUnique)
        .map(forceIdToString);

      hosts.forEach(host => {
        resources.forEach(resource => {
          const rule = getScheduleRuleFromDate(
            schedule.rules,
            date,
            host === DEFAULT_HOST_KEY ? undefined : host,
            resource === DEFAULT_RESOURCE_KEY ? undefined : resource
          );

          if (rule) {
            rule.slots
              .sort((a, b) => timeCompare(a.time, b.time))
              .forEach(slot => {
                // this handles date exceptions
                const exceptionCheck = slot.exceptions?.some(
                  ex =>
                    ex.date === date && (!isResourceType || safeIdCompare(ex.resource, resource))
                );

                if (!bypassDayChecks && exceptionCheck) return;

                timelineItems.push({
                  ...slot,
                  _id: new ObjectId().toHexString(),
                  slotId: slot._id,
                  ruleId: rule._id,
                  slotKey: buildReserveSlotKey(
                    {
                      date,
                      resource,
                      host,
                    },
                    slot.time,
                    slot.duration
                  ),
                  date: isoToDateUTC(date),
                  host: host === DEFAULT_HOST_KEY ? undefined : host,
                  resource: resource === DEFAULT_RESOURCE_KEY ? undefined : resource,
                  capacity: isResourceType ? 1 : slot.capacity,
                  overlaps: [], // will be filled later
                  reservations: [], // will be filled later
                  isFloatingSlot: false,
                });
              });

            // calculate overlaps between the slots
            const newItems = timelineItems.slice(timelineItems.length - rule.slots.length);
            for (let i = 0; i < newItems.length; i++) {
              for (let j = i + 1; j < newItems.length; j++) {
                if (
                  doTimeRangesOverlap(
                    newItems[i].time,
                    newItems[i].duration,
                    newItems[j].time,
                    newItems[j].duration
                  )
                ) {
                  newItems[i].overlaps.push(newItems[j].slotKey);
                  newItems[j].overlaps.push(newItems[i].slotKey);
                }
              }
            }
          }
        });
      });

      if (timelineItems.length > 0) {
        timelineEvents.push({
          template,
          items: timelineItems,
        });
      }
    });

    const timeline: ReserveTimeline = {
      date,
      events: timelineEvents,
    };

    timelines.push(timeline);

    // fill reservations into timeline
    (reservationsByDate[timeline.date] || []).forEach(res => {
      let event = timeline.events.find(e =>
        safeIdCompare(e.template._id, res.eventSnapshot.templateId)
      );
      const template = eventTemplateDict[res.eventSnapshot.templateId];

      if (!event && template) {
        timeline.events.push({
          template: eventTemplateDict[res.eventSnapshot.templateId],
          items: [],
        });
        event = timeline.events[timeline.events.length - 1];
      }

      const slotKey = slotKeyFromReservation(res);

      let item = event?.items.find(i => i.slotKey === slotKey);

      if (!item) {
        // this is a dangling reservation - slot that wasn't created in the step above because
        // it doesn't match the schedule rules, but we still want to show the reservation in the
        // timeline so we create a "fake" slot for it
        event?.items.push({
          _id: new ObjectId().toHexString(),
          slotId: new ObjectId().toHexString(),
          ruleId: new ObjectId().toHexString(),
          slotKey,
          date: isoToDateUTC(date),
          host: res.host,
          resource: res.resource,
          time: res.time,
          duration: res.duration,
          capacity: 0,
          overlaps: [],
          reservations: [],
          isFloatingSlot: true,
        });

        item = event?.items[event.items.length - 1];
      }

      item?.reservations.push(res);
    });
  });

  return timelines;
}

export function makeReservationFromOccurrence(
  reservation: Reservation,
  occurrence: ReservationOccurrence
): FlattenedReservation {
  const { additionalOccurrences, ...rest } = reservation;
  const thisOccurrenceIndex =
    additionalOccurrences?.findIndex(o => safeIdCompare(o._id, occurrence._id)) ?? -1;

  return {
    ...rest,
    ...occurrence,
    _isOccurrenceOf: reservation._id,
    additionalOccurrences: [],
    _totalOccurrences: (additionalOccurrences ?? []).length + 1,
    _isLastOccurrence:
      thisOccurrenceIndex === -1 ||
      thisOccurrenceIndex === (additionalOccurrences?.length ?? 0) - 1,
  };
}

export function flattenReservations(reservations: Reservation[]): FlattenedReservation[] {
  const flattened: FlattenedReservation[] = [];

  reservations.forEach(reservation => {
    const { additionalOccurrences, ...rest } = reservation;
    const occurrencesCount = additionalOccurrences?.length ?? 0;

    flattened.push({
      ...rest,
      _isOccurrenceOf: null,
      additionalOccurrences: [],
      _totalOccurrences: 1 + occurrencesCount,
      _isLastOccurrence: occurrencesCount === 0,
    });

    (additionalOccurrences ?? []).forEach(occurrence => {
      flattened.push(makeReservationFromOccurrence(reservation, occurrence));
    });
  });

  return flattened;
}

export function isFlattenedReservation(
  reservation: Reservation | FlattenedReservation
): reservation is FlattenedReservation {
  return (reservation as FlattenedReservation)._isOccurrenceOf !== undefined;
}

export function buildTimeOption(
  slot: ReserveCacheTimeslot,
  hosts?: MappedItems<BareEventHost>
): TimeOption {
  return {
    key: buildHostTimeSlotKeyPart(slot.host, slot.time, slot.duration),
    time: slot.time,
    duration: slot.duration,
    host: slot.host,
    timeLabel: formatTimeRange(slot.time, slot.duration),
    hostLabel: slot.host
      ? makeFullName(hosts?.[slot.host]?.firstName ?? '', hosts?.[slot.host]?.lastName ?? '')
      : undefined,
    spotsLeft: slot.spotsLeft,
    capacity: slot.capacity,
    label: slot.label,
  };
}

export function buildTicketSelections(
  tickets: ReservableTicket[],
  draftTickets?: { ticket: { _id: string }; quantity: number }[]
): TicketSelections {
  if (draftTickets) {
    return {
      quantities: draftTickets.reduce(
        (acc, t) => {
          acc[t.ticket._id] = t.quantity;
          return acc;
        },
        {} as Record<string, number>
      ),
    };
  }

  return {
    quantities: tickets.reduce(
      (acc, t) => {
        acc[t._id] = 0;
        return acc;
      },
      {} as Record<string, number>
    ),
  };
}

export function buildDateOption(
  isoDate: string,
  timeslots?: GroupedItems<ReserveCacheTimeslot>
): DateOption {
  const date = isoToDateLocal(isoDate);

  const timeOnlyKeys = (timeslots?.[isoDate] ?? [])
    .map(s => buildHostTimeSlotKeyPart(s.host, s.time, s.duration))
    .filter(onlyUnique);

  return {
    key: isoDate,
    date,
    weekday: date.toLocaleString('en-US', { weekday: 'short' }),
    monthDay: date.toLocaleString('en-US', { month: 'short', day: 'numeric' }),
    timesCount: timeOnlyKeys.length,
  };
}

export function getTimeOptions(
  timeslots: ReserveCacheTimeslot[],
  hosts?: MappedItems<BareEventHost>
) {
  const timeOptionsMap: Record<string, TimeOption> = {};

  for (const slot of timeslots) {
    const timeOption = buildTimeOption(slot, hosts);

    if (!timeOptionsMap[timeOption.key]) {
      timeOptionsMap[timeOption.key] = timeOption;
    } else {
      const objToModify = timeOptionsMap[timeOption.key];
      objToModify.spotsLeft += timeOption.spotsLeft;
      objToModify.capacity += timeOption.capacity;

      if (timeOption.label !== objToModify.label && !objToModify.label?.endsWith(' (& more)')) {
        objToModify.label = `${objToModify.label} (& more)`;
      }
    }
  }

  const options = Object.values(timeOptionsMap).sort(timeSorter);

  return { timeOptionsMap, options };
}

export function timeSorter<T extends { time: Time; duration?: number; hostLabel?: string }>(
  a: T,
  b: T
) {
  // sort by time first, then by duration, then by host
  const timeDiff = timeCompare(a.time, b.time);
  if (timeDiff !== 0) {
    return timeDiff;
    // longest duration first, with undefined durations (for open-ended slots) treated as 0
  } else if ((a.duration ?? 0) !== (b.duration ?? 0)) {
    return (b.duration ?? 0) - (a.duration ?? 0);
  } else {
    return (a.hostLabel ?? '').localeCompare(b.hostLabel ?? '');
  }
}

export function buildNeedsAttentionItems(timelines: ReserveTimeline[]): NeedsAttentionItem[] {
  const items: NeedsAttentionItem[] = [];

  timelines.forEach(timeline => {
    timeline.events.forEach(event => {
      event.items.forEach(slot => {
        const activeReservations = slot.reservations.filter(r => r.status !== 'cancelled');
        const ticketCount = activeReservations.reduce((sum, r) => sum + r.tickets.length, 0);

        if (slot.isFloatingSlot && activeReservations.length > 0) {
          items.push({
            type: 'unassigned',
            date: timeline.date,
            count: activeReservations.length,
            resource: slot.resource,
            reservations: activeReservations,
          });
        } else if (ticketCount > slot.capacity) {
          items.push({
            type: 'overcapacity',
            date: timeline.date,
            count: ticketCount,
            resource: slot.resource,
            capacity: slot.capacity,
            reservations: activeReservations,
          });
        }
      });
    });
  });

  return items;
}
